import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getFileThumbnail } from '../../services';

import FiletypeIcon from '../FiletypeIcon';
import { checkIsOnline } from '../../bundles/notifications';
import { MALWARE_SCAN_STATE, useMalwareScanState } from '../../bundles/files';

const FileThumbnail = ({
  file,
  online,
  iconSize = 'l',
  loaded = () => {
    // do something on loaded
  },
}) => {
  const dispatch = useDispatch();
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imgSource, setImgSource] = useState(null);
  // A file is only safe to show once the malware scan has finished and found nothing. Until
  // then, the file service refuses to hand out its thumbnail.
  //
  // So we only ask for the thumbnail of a clean file. If we asked for every file, the refused
  // requests would count as failed thumbnails (thumbnailError), and each failure would also make
  // the app run an "are we offline?" check, even though nothing is wrong with the connection.
  // Files that are not clean just show their normal file type icon instead.
  //
  // useMalwareScanState may ask the file service for the latest scan result when the file's
  // status is not final yet. That is only useful when this file is supposed to have a thumbnail
  // (isRecheckWanted). A file that never gets a thumbnail, such as a text file, would otherwise
  // trigger a pointless request.
  const malwareScanState = useMalwareScanState(file, { isRecheckWanted: file.shouldHaveThumbnail });
  const shouldHaveThumbnail =
    file.shouldHaveThumbnail && malwareScanState === MALWARE_SCAN_STATE.CLEAN;

  useEffect(() => {
    if (online) {
      setThumbnailError(false);
    }
  }, [online]);

  useEffect(() => {
    if (!shouldHaveThumbnail || thumbnailError) {
      loaded();
    }
  }, [thumbnailError, shouldHaveThumbnail]);

  useEffect(() => {
    setImgSource(null);
  }, [file.id]);

  useEffect(() => {
    let revokeUrl;
    let isMounted = true;
    async function fetchThumbnail() {
      try {
        const blob = await getFileThumbnail(file.id);
        const objectUrl = URL.createObjectURL(blob);
        if (isMounted) {
          setImgSource(objectUrl);
          revokeUrl = objectUrl;
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching thumbnail:', error);
          setThumbnailError(true);
          dispatch(checkIsOnline());
        }
      }
    }
    if (!thumbnailError && shouldHaveThumbnail) {
      fetchThumbnail();
    }
    return () => {
      isMounted = false;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [file.id, shouldHaveThumbnail, thumbnailError, dispatch]);

  const handleImgError = () => {
    setThumbnailError(true);
    dispatch(checkIsOnline());
  };

  if (!thumbnailError && shouldHaveThumbnail && imgSource) {
    return (
      <img
        src={imgSource}
        alt={file.filename || file.name || 'File thumbnail'}
        onLoad={loaded}
        onError={handleImgError}
      />
    );
  }

  return (
    <FiletypeIcon
      size={iconSize}
      file={file}
    />
  );
};

export default FileThumbnail;
